import { Delete, RemoveRedEye, ShowChart } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import CustomHeader from '../../../components/General/CustomHeader';
import CustomTable from '../../../components/General/CustomTable';
import ConversationModal from '../../../components/Highcharts/ConversationTab/conversationModal';
import Page from '../../../components/Layout/Page';
import apiClient from '../../../requests/api/apiClient';
import {
  ConversationSchema,
  TimePointSchema,
} from '../../../requests/api/apiTypes';
import { navigateToChart } from '../../../utils/helpers';
import useTableStyles from '../utils/styles';
import { TableRowsInfo, ReduxState } from '../../../types';
import { UserRole, UserType } from '../../../constants/user_types';
import { CONVERSATION_UI_LIST } from '../../../components/Highcharts/constants';

const ROWS_PER_PAGE = 5;

const CHART_MAP = {
  'Humedad Suelo Apilado': 'humedad-suelo-apilada',
  'Humedad Suelo': 'humedad-suelo',
  Riego: 'riego',
  Kc: 'kc',
  ET0: 'et0',
  Precipitaciones: 'precipitaciones',
  'Profundidad de Riego': 'profundidad-riego',
  'Dendrometro de brote': 'planta',
};

const ConversationsListView = () => {
  const history = useHistory();
  const classes = useTableStyles();
  const location = useLocation();
  const [paginationParameters, setPaginationParameters] = useState({
    page: 0,
    rowsPerPage: ROWS_PER_PAGE,
  });
  const [modalState, setModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [tableData, setTableData] = useState<TableRowsInfo>({
    values: [],
    count: 0,
  } as TableRowsInfo);
  const [loading, setLoading] = useState(false);
  const [selectedElem, setSelectedElem] = useState<ConversationSchema | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string>('');
  const [deleting, setDeleting] = useState(false);

  // Get current user to check if they are admin
  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const selectedFarm = useSelector((state: ReduxState) => state.accountFarm.selectedFarm);
  const selectedAccount = useSelector(
    (state: ReduxState) => state.userAccount.selectedAccount,
  );
  const isAdmin = currentUser?.role === UserRole.ADMIN && currentUser?.type === UserType.CDTEC;

  const openDeleteDialog = (conversationId: string) => {
    setConversationToDelete(conversationId);
    setDeleteDialogOpen(true);
  };

  const oneButtonAction = (elem: ConversationSchema) => (
    <Box display='flex' alignItems='center' justifyContent='center'>
      <IconButton
        onClick={() => {
          setSelectedElem(elem);
          setModalState({
            open: true,
            conversationId: elem.conversationId,
            timePointData: elem.timePoint,
          });
        }}>
        <RemoveRedEye />
      </IconButton>
      <IconButton
        onClick={() => {
          navigateToChart(CHART_MAP[elem.chart as keyof typeof CHART_MAP], history, {
            farmId: elem.farmId,
            sectorId: elem.sectorId,
            wiseconnZoneId: elem.wiseconnId,
            date: `${elem.timePoint.day}-${elem.timePoint.month}-${elem.timePoint.year}`,
            source: 'notification',
          });
        }}>
        <ShowChart />
      </IconButton>
      {isAdmin && (
        <IconButton
          onClick={() => openDeleteDialog(elem.conversationId)}
          color="error">
          <Delete />
        </IconButton>
      )}
    </Box>);

  const loadData = async () => {
    setLoading(true);
    const urlParams = new URLSearchParams(location.search);
    const farmIdFromQuery = urlParams.get('farmId') || selectedFarm?.id;
    const { data: fetchData } = await apiClient.conversation.conversationsList({
      page: paginationParameters.page,
      rowsPerPage: paginationParameters.rowsPerPage,
      farmId: farmIdFromQuery || undefined,
      accountId: selectedAccount?.id || undefined,
    });
    const localData = {
      count: fetchData?.totalConversations ?? 0,
      values: fetchData?.conversations?.filter((elem) => elem.conversationId).map((elem) => ({
        conversationId: elem.conversationId,
        updatedAt: dayjs(elem.updatedAt).format('DD/MM/YYYY HH:mm'),
        updatedBy: elem.updatedBy,
        lastComment: elem.lastComment,
        farmName: elem.farmName,
        farmId: elem.farmId,
        sectorId: elem.sectorId,
        sectorName: elem.sectorName ?? 'N/A',
        chart: elem.chart,
        seeMore: oneButtonAction(elem),
      })) ?? [],
    };
    setTableData(localData);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [
    paginationParameters.page,
    paginationParameters.rowsPerPage,
    selectedFarm?.id,
    selectedAccount?.id,
    location.search,
  ]);

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;

    setDeleting(true);
    try {
      await apiClient.conversation.conversationDelete(conversationToDelete);
      setDeleteDialogOpen(false);
      setConversationToDelete('');
      // Refresh the data by updating pagination parameters
      setPaginationParameters((prev) => ({ ...prev }));
      loadData();
    } catch (error) {
      console.error('Error deleting conversation:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Page
      className={classes.root}
      title='Conversaciones'
    >
      <Container maxWidth={false}>
        <CustomHeader title='Conversaciones' showBackButton={false} />
        <Box mt={3}>
          <Card className={classes.cardContainer}>
            <Divider />
            {loading ? (
              <Box display="flex" justifyContent="center" m={3}>
                <CircularProgress />
              </Box>
            ) : (
              <CustomTable
                rows={{ values: tableData.values, count: tableData.count }}
                headers={CONVERSATION_UI_LIST}
                paginationParameters={paginationParameters}
                setPaginationParameters={setPaginationParameters}
              />
            )}
          </Card>
        </Box>
        {selectedElem && modalState.open && (
          <ConversationModal
            modalState={modalState}
            setModalState={setModalState}
            farmId={selectedElem.farmId}
            sectorId={selectedElem.sectorId}
            chart={selectedElem.chart}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Confirmar eliminación
          </DialogTitle>
          <DialogContent>
            <Typography id="delete-dialog-description">
              ¿Estás seguro de que quieres eliminar esta conversación?
              Esta acción no se puede deshacer y eliminará todos los comentarios asociados.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteConversation}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? <CircularProgress size={20} /> : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default ConversationsListView;
